import { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LogIn, Eye } from "lucide-react";
import { useGuestMode } from "@/hooks/useGuestMode";
import { capturePostHog } from "@/lib/posthog";

/**
 * The guest→account moment. Conversion audit 2026-08-10: "Try it now" is the
 * most-clicked CTA on the Gateway, so guest mode is the main front door — but
 * this dialog asked every guest the same abstract question ("Ready to Start
 * Your Transformation?") regardless of what they'd just reached for, and
 * nothing about it was measured.
 *
 * Two changes: the copy now names the thing they were doing (you save the
 * check you just tried to tick — not "start a journey"), and both the open
 * and the CTA fire PostHog events so guest→signup is finally a visible
 * funnel. Copy stays no-punish: it's an offer to keep progress, never a
 * warning about losing it.
 */

/** Per-action copy. `null` action falls back to the generic pair. */
const ACTION_COPY: Record<string, { title: string; body: string; cta: string }> = {
  "complete-habit": {
    title: "Save that check",
    body: "Create a free account and today counts — your habit, your streak, and every day after it.",
    cta: "Save my progress",
  },
  "create-habit": {
    title: "Keep this habit",
    body: "A free account saves the habit you just built and starts counting from today.",
    cta: "Save my habit",
  },
  "goals": {
    title: "Keep your goals",
    body: "Free account, and your goals are here tomorrow — with the progress you make against them.",
    cta: "Save my goals",
  },
};

const GENERIC = {
  title: "Keep what you've started",
  body: "Create a free account so today's progress is still here tomorrow. No credit card, 30 seconds.",
  cta: "Create free account",
};

export default function RegisterPrompt() {
  const { showRegisterPrompt, setShowRegisterPrompt, registerPromptAction } = useGuestMode();
  const copy = (registerPromptAction && ACTION_COPY[registerPromptAction]) || GENERIC;

  // One `shown` per impression. Keyed on open/close only — a second
  // requireAuth() firing while the dialog is ALREADY open changes the action
  // but is not a new impression, and counting it inflated the funnel's
  // denominator (review 2026-08-10).
  useEffect(() => {
    if (!showRegisterPrompt) return;
    capturePostHog("guest.register_prompt.shown", { action: registerPromptAction ?? "generic" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see note above
  }, [showRegisterPrompt]);

  // Every close path must report, or the funnel this component exists to
  // measure under-counts dismissals: the Dialog also closes on Escape, on an
  // overlay click and via the built-in X, none of which route through the
  // button handler below (review 2026-08-10).
  const handleOpenChange = (next: boolean) => {
    if (!next && showRegisterPrompt) {
      capturePostHog("guest.register_prompt.dismissed", {
        action: registerPromptAction ?? "generic",
        via: "dialog",
      });
    }
    setShowRegisterPrompt(next);
  };

  return (
    <Dialog open={showRegisterPrompt} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground text-center">
            {copy.title}
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            {copy.body}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <Button
            onClick={() => {
              capturePostHog("guest.register_prompt.cta", { action: registerPromptAction ?? "generic" });
              // Bypass handleOpenChange: this is a conversion, not a dismissal.
              setShowRegisterPrompt(false);
              window.location.href = "/login?mode=signup";
            }}
            className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white font-semibold py-3"
          >
            <LogIn className="w-4 h-4 mr-2" />
            {copy.cta}
          </Button>
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            className="w-full text-muted-foreground"
          >
            <Eye className="w-4 h-4 mr-2" />
            Keep looking around
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground/60 text-center">
          Free to start · No credit card · Rest days never break your streak
        </p>
      </DialogContent>
    </Dialog>
  );
}
